import { Injectable, OnDestroy } from '@angular/core';
import { LoggerService } from '@app/blocks/logger/services/logger.service';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { SignupDataService } from './signup-data.service';
import { Location } from '@angular/common';
import { PaymentService } from '@app/payments/services/payment.service';
import { UtilityService } from '@app/shared/services/utility/utility.service';
import { Adb2cService } from '@app/blocks/adb2c/services/adb2c/adb2c.service';

@Injectable({
    providedIn: 'root'
})
export class SignupService implements OnDestroy {

    additionalDetailsSubject = new Subject();
    emergencyContactsSubject = new Subject();
    nextOfKinSubject = new Subject();
    paymentMethodSubject = new Subject();
    refDataSubject = new Subject();

    additionalDetailsModel: any = {};
    nextOfKinModel: any = {};
    emergencyContactsModel: any = {};
    paymentMethodModel: any = {};
    refDataModel: any = {};

    private destroy$ = new Subject<void>();

    formState = {
        applicationForm: "general",
        "progress": {
            "additionalDetailsComplete": false,
            "emergencyContactsComplete": false,
            "nextOfKinComplete": false,
            "paymentMethodComplete": false,
            "firstPaymentComplete": false
        }
    };

    active = { "active": "additionalDetails" };

    constructor(private logger: LoggerService,
        private signupDataService: SignupDataService,
        private paymentsService: PaymentService,
        private location: Location,
        private utilityService: UtilityService,
        private adb2cService: Adb2cService) {
        this.adb2cService.getUser();
    }
    ngOnDestroy(): void {
        throw new Error('Method not implemented.');
    }

    // back slide functionality
    goBack(currentSlide: string): void {
        if (currentSlide === 'emergencyContacts') {
            this.active.active = 'additionalDetails'; // set new active slide
        }
        if (currentSlide === 'nextOfKin') {
            this.active.active = 'additionalDetails'; // set new active slide
        }

        if (currentSlide === 'paymentMethod') {
            let slide = 'emergencyContacts';
            if (this.formState.applicationForm == "general") {
                slide = 'nextOfKin';
            }
            this.active.active = slide; // set new active slide
        }
        if (currentSlide === 'firstPayment') {
            this.active.active = 'paymentMethod'; // set new active slide
        }
    }

    // next slide functionality, called via onSubmit when form is valid
    goForward(currentSlide: string): void {
        this.logger.info("signup.service :: goForward :: currentSlide " + currentSlide);

        if (currentSlide === 'additionalDetails') {
            if (this.formState.applicationForm == "general") {
                this.loadNextOfKin();
            }
            else {
                this.loadEmergencyContacts();
            }
        }

        if (currentSlide === 'emergencyContacts') {
            this.loadPaymentMethod();
        }

        if (currentSlide === 'nextOfKin') {
            this.loadPaymentMethod();
        }

        if (currentSlide === 'paymentMethod') {
            this.adb2cService.activeUser.asObservable().pipe(takeUntil(this.destroy$)).subscribe(user => {
                const userId = user?.sub ?? user?.oid;
                if (user && userId) {
                    firstValueFrom(this.signupDataService.getApplicationStatus(this.additionalDetailsModel?.applicationId, userId)).then((response: any) => {
                        const order = this.paymentsService.getOrder();
                        order.amount = 50; // initalise as �50 so it can never be zero

                        const crmOrderAmount = parseFloat(response.advancedPaymentCharge);

                        if (crmOrderAmount > 0) { // set to CRM value if not 0
                            order.amount = crmOrderAmount;
                        }

                        order.tenancyReference = this.additionalDetailsModel.applicationId;
                        this.paymentsService.setOrder(order);

                        this.active.active = 'firstPayment';

                    }).catch((error: any) => {
                        console.log(error);
                    })
                }
            });
        }
    }

    loadEmergencyContacts() {
        this.logger.info('goForward signup emergencyContacts');

        firstValueFrom(this.signupDataService.getEmergencyContacts(this.additionalDetailsModel.applicationId))
            .then((value: any) => {
                this.logger.info('goForward emergencyContacts model');
                this.setEmergencyContactsModel(value);
                // scroll(active.active, $timeout, $document, $location);
            });

        this.active.active = 'emergencyContacts';
    }

    loadNextOfKin() {
        firstValueFrom(this.signupDataService.getNextOfKin(this.additionalDetailsModel.applicationId))
            .then((value: any) => {
                this.logger.info('goForward signup nextOfKin');
                this.setNextOfKinModel(value);
                // scroll(active.active, $timeout, $document, $location);
            });

        this.active.active = 'nextOfKin';
    }

    loadPaymentMethod() {
        firstValueFrom(this.signupDataService.getPaymentMethod(this.additionalDetailsModel.applicationId))
            .then((response) => {
                this.logger.info('goForward paymentMethod model', response);
                this.setPaymentMethodModel(response);
                //this.scroll(this.active.active, timeout, document, $location);
            });
        this.active.active = 'paymentMethod';
    }


    isComplete(slide: string) {
        let result = false;

        if (slide === 'additionalDetails') {
            result = this.formState.progress.additionalDetailsComplete;
        }

        if (slide === 'nextOfKin') {
            result = this.formState.progress.nextOfKinComplete;
        }

        if (slide === 'paymentMethod') {
            result = this.formState.progress.paymentMethodComplete;
        }

        if (slide === 'firstPayment') {
            result = this.formState.progress.firstPaymentComplete;
        }

        this.logger.info('isComplete ' + result);

        return result;
    }

    getActive() {
        return this.active;
    }

    setActive(slide: string) {
        this.active.active = slide;
    }

    getFormState() {
        return this.formState;
    }

    setFormState(data: any) {
        Object.assign(this.formState, data);
    }

    getAdditionalDetailsModel() {
        return this.additionalDetailsModel;
    }

    setAdditionalDetailsModel(data: any) {
        this.logger.info('setAdditionalDetailsModel called');
        this.additionalDetailsModel = data;
        this.logger.info('additionalDetailsModel', this.additionalDetailsModel);
        this.additionalDetailsSubject.next(data);
    }

    subscribeAdditionalDetailsModel(obj: any) {
        return this.additionalDetailsSubject.subscribe(obj);
    }

    getRefDataModel() {
        this.logger.info('getRefDataModel called', this.refDataModel);
        return this.refDataModel;
    }

    setRefDataModel(data: any) {
        this.logger.info('setRefDataModel called');
        this.refDataModel = data;

        this.logger.info('setRefDataModel result', this.refDataModel);
        this.refDataSubject.next(data);
    }

    subscribeRefDataModel(obj: any) {
        this.logger.info('subscribeRefDataModel');
        return this.refDataSubject.subscribe(obj);
    }

    getEmergencyContactsModel() {
        return this.emergencyContactsModel;
    }

    setEmergencyContactsModel(data: any) {
        this.logger.info('EmergencyContactsModel called', data);
        this.emergencyContactsModel = data;
        this.logger.info('EmergencyContactsModel', this.emergencyContactsModel);
        this.emergencyContactsSubject.next(data);
    }

    subscribeEmergencyContactsModel(obj: any) {
        this.logger.info("Subscribe Emergency Contacts Model")
        return this.emergencyContactsSubject.subscribe(obj);
    }

    getNextOfKinModel() {
        return this.nextOfKinModel;
    }

    setNextOfKinModel(data: any) {
        this.logger.info('setNextOfKinModel called');
        this.nextOfKinModel = data;

        this.logger.info('nextOfKinModel', this.nextOfKinModel);
        this.nextOfKinSubject.next(data);
    }

    subscribeNextOfKinModel(obj: any) {
        return this.nextOfKinSubject.subscribe(obj);
    }

    // Payment Method
    getPaymentMethodModel() {
        return this.paymentMethodModel;
    }

    setPaymentMethodModel(data: any) {
        this.logger.info('setPaymentMethodModel called');
        this.paymentMethodModel = data;
        this.logger.info('paymentMethodModel', this.paymentMethodModel);
        this.paymentMethodSubject.next(data);
    }

    subscribePaymentMethodModel(obj: any) {
        return this.paymentMethodSubject.subscribe(obj);
    }

    addAddressIds(model: any) {
        this.logger.info('addAddressIds before', model);

        // eslint-disable-next-line no-prototype-builtins
        if (!model.hasOwnProperty('addressId') && model.addressId !== "") {
            this.logger.info('addAddressIds > adding id');

            model.addressId = this.utilityService.newGuid();

            this.logger.info('addAddressIds > result', model);
        }
        this.logger.info('addAddressIds after', model);
        return model;
    }

}
